<template>
  <UIFormField
    :label="label"
    :field-type="fieldType"
    :name="name"
    :rules="rules"
    :error-message="errorMessage"
    :no-margin="noMargin"
  >
    <UIDropDown
      ref="dropdownComponent"
      :value="isOpened"
      :class="{
        select: true,
        opened: isOpened,
        select_disabled: disabled,
      }"
      :loading="loading"
      :disabled="disabled"
      :max-height="maxHeight"
      @input="isOpened = $event"
    >
      <template #activator>
        <div
          class="select__value"
          :class="{ 'select__value_error': hasError || !!errorMessage }"
        >
          {{ valueText || placeholderComp }}
        </div>
      </template>

      <DropdownList
        :model-value="value"
        :values="filteredValues"
        @select="selectItem($event.id)"
      >
        <UISearchInput
          v-if="hasSearch"
          v-model="searchQuery"
          :placeholder="searchPlaceholderComp"
          class="select__list-search"
        />
      </DropdownList>
    </UIDropDown>
  </UIFormField>
</template>

<script setup lang="ts">
import { useField } from 'vee-validate';
import { FieldType, SelectValueItem } from '~/types/components/form';
import UIDropDown from '~/components/ui/UIDropDown.vue';
import UISearchInput from '~/components/ui/UISearchInput.vue';
import UIFormField from '~/components/form/UIFormField.vue';
import DropdownList from '~/components/form/DropdownList.vue';

const props = withDefaults(
  defineProps<{
    name?: string,
    label?: string,
    modelValue: string | number | null,
    values: SelectValueItem[],
    placeholder?: string,
    hasSearch?: boolean,
    searchPlaceholder?: string,
    fieldType?: FieldType,
    rules?: string,
    loading?: boolean,
    disabled?: boolean,
    hasError?: boolean,
    maxHeight?: string,
    noMargin?: boolean,
  }>(),
  {
    name: '',
    label: '',
    placeholder: '',
    modelValue: '',
    searchPlaceholder: '',
    fieldType: undefined,
    rules: undefined,
    hasSearch: false,
    isError: false,
    noMargin: false,
    maxHeight: '',
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | null): void,
  (e: 'select', value: SelectValueItem | null): void,
}>();

const { t } = useI18n();
const { errorMessage, value } = useField(toRef(props, 'name'), toRef(props, 'rules'), {
  initialValue: props.modelValue,
  syncVModel: true,
});

const dropdownComponent = ref<InstanceType<typeof UIDropDown> | null>();
const isOpened = ref<boolean>(false);
const searchQuery = ref<string>('');

const placeholderComp = computed(() => props.placeholder ?? t('form.Select.choose'));
const searchPlaceholderComp = computed(() => props.searchPlaceholder ?? t('form.Select.search'));

const selectedVal = computed<SelectValueItem | null>(() => props.values.find((v) => v.id === value.value) ?? null);
const valueText = computed<string | number>(() => {
  if (selectedVal.value) {
    return selectedVal.value.name;
  }
  return placeholderComp.value ?? '';
});
const filteredValues = computed<SelectValueItem[]>(
  () => (props.hasSearch && searchQuery.value.length
    ? props.values.filter((val) => val.name.toLowerCase().includes(searchQuery.value.toLowerCase()))
    : props.values),
);

async function selectItem(id: string | number | null) {
  if (id === value.value) return;

  value.value = id;
  dropdownComponent.value?.hide();

  if (id !== props.modelValue) {
    await nextTick();
    emit('select', selectedVal.value);
  }
}

watch(() => props.modelValue, selectItem, { immediate: true });
</script>

<style scoped lang="scss">
.select {
  width: 100%;

  &__value {
    position: relative;
    border: 1px solid #e6e6e6;
    border-radius: 2px;
    padding: 0 30px 0 10px;
    height: 40px;
    line-height: 40px;
    font-size: 14px;
    color: #333;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    &_error {
      border-color: #f00;
    }

    &::after {
      display: block;
      width: 12px;
      height: 7px;
      position: absolute;
      top: 50%;
      right: 10px;
      content: '';
      background: url(~/assets/img/arrow-bracket.svg) no-repeat;
      transform: rotateZ(180deg) translateY(3px);
      transition: transform 0.2s ease-in-out;
    }
  }

  &__list-search {
    margin: 13px 10px;
    width: calc(100% - 20px);
    line-height: 34px;
    height: 34px;
  }

  &.opened .select__value{
    border-color: #333;

    &::after {
      transform: rotateY(0deg) translateY(-3px);
    }
  }

  &_disabled {
    .select__value {
      &:after {
        display: none;
      }
    }
  }
}
</style>
